import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingBackdrop } from '@components/LoadingBackdrop/LoadingBackdrop';
import { useToastContext } from '@context/toastContext';
import { ActionDialogProps, ActionMenuProps } from '@components/ActionMenu/ActionMenu';
import { isErrorResponse, safelyParseJson } from '@utils/utils';
import { useScenarioEventCopy } from '@features/scenario-presets/scenario-events-copy-action/model/useScenarioEventCopy';
import { ScenarioEventsEntityName } from '@api/types/types';
import Button from '@components/buttons/Button';

const CopyScenarioEventActionFabric =
    ({ entityName }: { entityName: ScenarioEventsEntityName }) =>
    ({ id, loadEntitiesFn }: ActionMenuProps) => {
        const CopyScenarioEventAction = ({ handleCloseDialog }: ActionDialogProps) => {
            const { loading, copyScenarioEvent } = useScenarioEventCopy();
            const { addToast } = useToastContext();
            const { t } = useTranslation();

            const cancelHandler = useCallback(
                event => {
                    handleCloseDialog(event);
                },
                [handleCloseDialog]
            );

            const deleteHandler = useCallback(
                async event => {
                    const resp = await copyScenarioEvent({
                        eventId: id,
                        entityName: entityName as ScenarioEventsEntityName,
                    });

                    if (isErrorResponse(resp)) {
                        const error =
                            typeof resp.error === 'string'
                                ? safelyParseJson(resp.error)
                                : resp.error;
                        addToast(`${error?.['details'] ?? t('common.save-error')}`, 'error');
                    } else {
                        await loadEntitiesFn();
                        addToast(`${t('common.save-successful')}`, 'success');
                    }

                    handleCloseDialog(event);
                },
                [handleCloseDialog, entityName]
            );

            return (
                <LoadingBackdrop isVisible={loading} transparent>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <Button
                            onClick={cancelHandler}
                            text={t('common.cancel')}
                            colorType="transparent"
                        />
                        <Button
                            type="submit"
                            onClick={deleteHandler}
                            text={t('scenario-case-events.copy-scenario-event-action.action-title')}
                            colorType="dark"
                        />
                    </div>
                </LoadingBackdrop>
            );
        };

        return CopyScenarioEventAction;
    };

export default CopyScenarioEventActionFabric;
